import type { Store } from 'effector'
import type { CategoryWithShowsFormatted } from '~/entities/shows/categories'

import { api } from '@setplex/tria-api'
import { combine, createEvent, createStore, sample } from 'effector'
import { and, combineEvents, not } from 'patronum'
import { options } from '~/core/flags'
import { model as i18n } from '~/core/i18n'
import { register } from '~/core/repository'
import { model as router } from '~/core/router'
import { model as bannersModel } from '~/entities/banners'
import { entitiesPlayerTrailerModel } from '~/entities/player/trailer'
import '~/entities/shows'
import { model as tvShowBundlesModel } from '~/entities/shows/bundles'
import { formatCategoriesWithShows } from '~/entities/shows/categories'
import { model as tvShowFeaturedModel } from '~/entities/shows/featured'
import * as mainModel from '~/entities/shows/model'
import { model as rootEntity } from '~/entities/shows/root'
import { ssePurchasesModel } from '~/features/payment/sse-purchases'
import '~/widgets/Banners/BannerRowContainerWithTrailerPortal'
import * as pinLockFeature from '~/features/pin-lock'
import { genPagesLogic } from '~/shared/effector'
import { noop } from '~/shared/helpers'
import '~/shared/ui/translations/general-translations'

const DEFAULT_ROW_ELEMENTS_COUNT = 36
const DEFAULT_ROWS_COUNT = 9

// *
// * EVENTS
// *

export const init = createEvent<{ query: URLSearchParams }>()

export const load = createEvent<{
  page: number
  count?: number
  mockCategoryId?: number
}>()

// Reset pin-lock when slide changes in banner carousel
sample({
  clock: entitiesPlayerTrailerModel.onBannerSlideChanged,
  target: pinLockFeature.resetUnlockId,
})

const seeAllTvShowClick = createEvent<{ id: number }>()
const seeAllTvShowCategoryClick = createEvent<{ id: number }>()
export const left = createEvent()

// *
// * STORES
// *

// store with categories and shows, with adjusted category names
export const $categoriesWithShowsFormatted: Store<
  CategoryWithShowsFormatted[] | null
> = combine(
  api.tvshows.$tvShowCategoriesWithShows,
  ssePurchasesModel.$tvShow,
  i18n.$t,
  (categories, adjust, { t }) =>
    formatCategoriesWithShows({ categories, adjust, t })
)

export const playButtonClick = createEvent<{
  id: number
}>()

sample({
  clock: playButtonClick,
  fn: ({ id }) => `/player/series?tvshowId=${id}`,
  target: router.navigatePush,
})

// *
// * CONNECTIONS
// *

sample({
  clock: init,
  fn: ({ query }) => ({
    page: Number(query.get('page')) || 0,
    count: Number(query.get('count')) || DEFAULT_ROWS_COUNT,
  }),
  target: load,
})

sample({
  clock: load,
  filter: not(rootEntity.getBannersFx.pending),
  fn: noop,
  target: rootEntity.getBannersFx,
})
sample({
  clock: load,
  filter: not(rootEntity.getFeaturedCarouselFx.pending),
  fn: noop,
  target: rootEntity.getFeaturedCarouselFx,
})
sample({
  clock: load,
  filter: not(rootEntity.initCategoriesWithShowsFx.pending),
  target: rootEntity.initCategoriesWithShowsFx,
})
sample({
  clock: load,
  filter: not(rootEntity.initShowBundlesRowByPageFx.pending),
  fn: (clockData) => ({ ...clockData, count: DEFAULT_ROW_ELEMENTS_COUNT }),
  target: rootEntity.initShowBundlesRowByPageFx,
})
sample({
  clock: load,
  filter: not(rootEntity.initInfiniteShowsCategoryFx.pending),
  fn: (clockData) => ({ ...clockData, count: DEFAULT_ROW_ELEMENTS_COUNT }),
  target: rootEntity.initInfiniteShowsCategoryFx,
})

const loadMoreCategoriesWithShows = genPagesLogic(
  api.tvshows.$pageInfoCategoriesWithShows,
  api.tvshows.loadCategoriesWithShowsFx
)

// eslint-disable-next-line effector/no-watch
// TODO: redo to navigateFx
sample({
  clock: seeAllTvShowClick,
  fn: ({ id }) => `/content/series/categories/${id}`,
  target: router.navigatePush,
})
sample({
  clock: seeAllTvShowCategoryClick,
  fn: ({ id }) => `/content/series/categories/${id}/subcategories`,
  target: router.navigatePush,
})

// *
// * SKIN
// *

export const $bannerShowsCarouselShown = and(
  options.$bannerCarouselsEnabled,
  bannersModel.$bannersTypeShowFormatted.map((banners) =>
    banners.some(({ bannerBundles }) => bannerBundles?.length)
  )
)

export const $bannerCarousels = bannersModel.$bannersTypeShowFormatted

const $featuredShowsCarouselShown = and(
  options.$featuredCarouselsEnabled,
  api.banners.$featuredCarouselTypeTvShow.map((carousel) =>
    carousel?.some((item) => item.enabledOnModulePage)
  )
)

const $infiniteShowsCategoryShown = and(
  api.tvshows.$pageInfoCategoriesWithShows.map((pageInfo) =>
    Boolean(pageInfo?.last)
  ),
  mainModel.$infiniteShowsFormatted.map((infiniteShows) =>
    Boolean(infiniteShows?.content?.length)
  )
)

const showsCatalogInitsSettled = combineEvents({
  events: {
    banners: rootEntity.getBannersFx.finally,
    featured: rootEntity.getFeaturedCarouselFx.finally,
    categories: rootEntity.initCategoriesWithShowsFx.finally,
    bundles: rootEntity.initShowBundlesRowByPageFx.finally,
    infinite: rootEntity.initInfiniteShowsCategoryFx.finally,
  },
})

export const $showsCatalogSettled = createStore(false).reset(init)

sample({
  clock: showsCatalogInitsSettled,
  fn: () => true,
  target: $showsCatalogSettled,
})

const $anyVisibleShowsShelf = combine(
  $bannerShowsCarouselShown,
  $featuredShowsCarouselShown,
  tvShowBundlesModel.$showBundlesRowFormatted,
  $categoriesWithShowsFormatted,
  $infiniteShowsCategoryShown,
  (banners, featured, bundles, categories, infinite) =>
    banners ||
    featured ||
    bundles.length > 0 ||
    (categories?.length ?? 0) > 0 ||
    infinite
)

export const $isShowsEmptyPage = combine(
  $showsCatalogSettled,
  $anyVisibleShowsShelf,
  (settled, anyVisible) => settled && !anyVisible
)

// clear paginatable stores
api.tvshows.$tvShowCategoriesWithShows.reset(init) // not necessary
api.showBundles.$showBundlesRow.reset(init) // not necessary
api.banners.$bannersTypeTvShow.reset(init)

register(tvShowFeaturedModel.$featuredTvShowsFormatted, '$shows/index/featured')
register(
  tvShowFeaturedModel.$featuredTvShowsTitle,
  '$shows/index/featured-title'
)
register($categoriesWithShowsFormatted, '$shows/index/categories+shows')
register(tvShowBundlesModel.$showBundlesRowFormatted, '$showBundlesRow')
register(tvShowBundlesModel.$showBundlesRowTitle, '$showBundlesRowTitle')
register($featuredShowsCarouselShown, '$shows/index/isShowsFeaturedCarousel')
register($bannerShowsCarouselShown, '$bannerShowsCarouselShown')
register(seeAllTvShowClick, 'shows/index/seeAll')
register(playButtonClick, 'shows/index/playButtonClick')
register(seeAllTvShowCategoryClick, 'shows/index/seeAllCategory')
register(loadMoreCategoriesWithShows, 'loadMoreCategoriesWithShows')
register(
  api.tvshows.$pageInfoCategoriesWithShows,
  '$pageInfoCategoriesWithShows'
)
register(mainModel.$infiniteShowsFormatted, '$infiniteShowsFormatted')
register(mainModel.$infiniteShowsTitle, '$infiniteShowsTitle')
register($infiniteShowsCategoryShown, '$infiniteShowsCategoryShown')
register($isShowsEmptyPage, '$isShowsEmptyPage')
